import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import './SignInModal.css';

const FRIENDLY_ERRORS = {
  'auth/email-already-in-use': 'An account already exists for that email — try signing in instead.',
  'auth/invalid-email': 'That doesn’t look like a valid email address.',
  'auth/weak-password': 'Please use at least 6 characters for your password.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/user-not-found': 'No account found for that email — try signing up instead.',
  'auth/popup-closed-by-user': 'Google sign-in was closed before finishing.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
};

const friendlyError = (err) => FRIENDLY_ERRORS[err?.code] || 'Something went wrong. Please try again.';

/**
 * Sign-in/sign-up overlay. Rendered conditionally in-place by the caller (never a route),
 * so whatever lead form triggered it keeps its typed state underneath while this is open.
 */
export default function SignInModal({ open, onClose }) {
  const { user, isPasswordAccount, isAuthSatisfied, signIn, signUp, signInWithGoogle, resendVerification, refreshUser } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);

  if (!open) return null;

  const showVerifyPending = user && isPasswordAccount && !isAuthSatisfied;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signUp(email.trim(), password);
      } else {
        await signIn(email.trim(), password);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      setResent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    setBusy(true);
    setError('');
    try {
      const refreshed = await refreshUser();
      if (!refreshed?.emailVerified) {
        setError('Still not verified — click the link in the email we sent you, then press Continue again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="signin-modal__overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="signin-modal">
        <button type="button" className="signin-modal__close" aria-label="Close" onClick={onClose}>&times;</button>

        {showVerifyPending ? (
          <div className="signin-modal__verify">
            <h3>Verify your email</h3>
            <p>We sent a verification link to <strong>{user.email}</strong>. Click it, then come back and press Continue.</p>
            {resent ? <p className="signin-modal__hint">Verification email resent.</p> : null}
            {error ? <p className="signin-modal__error">{error}</p> : null}
            <button type="button" className="signin-modal__primary" onClick={handleContinue} disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
            <button type="button" className="signin-modal__link" onClick={handleResend} disabled={busy}>
              Resend verification email
            </button>
          </div>
        ) : (
          <>
            <h3>{mode === 'signup' ? 'Create your account' : 'Sign in to continue'}</h3>
            <p className="signin-modal__sub">
              {mode === 'signup'
                ? 'One quick step so we can reply to the right inbox.'
                : 'Sign in to send your request.'}
            </p>

            <button type="button" className="signin-modal__google" onClick={handleGoogle} disabled={busy}>
              <GoogleIcon /> Continue with Google
            </button>

            <div className="signin-modal__divider"><span>or</span></div>

            <form onSubmit={handleSubmit} className="signin-modal__form">
              <label>
                <span>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
              </label>
              <label>
                <span>Password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
              </label>
              {error ? <p className="signin-modal__error">{error}</p> : null}
              <button type="submit" className="signin-modal__primary" disabled={busy}>
                {busy ? 'Please wait…' : mode === 'signup' ? 'Sign up' : 'Sign in'}
              </button>
            </form>

            <button type="button" className="signin-modal__link" onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(''); }}>
              {mode === 'signup' ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.95 10.7a5.4 5.4 0 0 1 0-3.4V4.97H.94a9 9 0 0 0 0 8.06l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .94 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z" />
    </svg>
  );
}
